import { request } from '@mairistem/tools';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';

import { FactureContext, LoadingContext, RecuContext, RegieContext } from '@/contexts';
import type { RecuContextType } from '@/contexts/RecuContext';
import useListeSearchSort from '@/hooks/useListeSearchSort';
import { getRecus, postAnnulationRecu, postRejetPrelevementRecu, searchRecus } from '@/routes';
import type { RecuEncaissementType } from '@/types';
import { getRecusListeDefaultFilters } from '@/utils';
import { useRegieStore, useRegisseurStore } from '@/zustand';

type RecuContextProviderProps = {
  children: React.ReactNode;
  /** En mode standAlone, le context ne gère pas la liste de reçu en interne */
  standAlone?: boolean;
  defaultFilteredOnRegisseur?: boolean;
  /**
   * Restreint la liste aux reçus postérieurs au dernier arrêté comptable.
   * Utilisé par la page d'encaissement ; la liste globale des reçus ne l'active pas
   * afin d'afficher tous les reçus de la régie.
   */
  depuisDernierArrete?: boolean;
};

const RecuContextProvider = (props: RecuContextProviderProps) => {
  const {
    children,
    defaultFilteredOnRegisseur = true,
    standAlone = false,
    depuisDernierArrete = false,
  } = props;

  const [defaultFiltersApplied, setDefaultFiltersApplied] = React.useState(false);

  const connectedRegisseur = useRegisseurStore((state) => state.regisseur);

  // Procédure d'arrêté annuel de rattrapage (Cas 1) : les annulations de reçus dans le contrôle
  // des règlements doivent être antidatées à la fin d'exercice (configurable, cf. arreteConfig).
  const arreteAnnuelRequis = useRegieStore((state) => state.regie?.arreteAnnuelRequis);
  const arreteAnnuelCas = useRegieStore((state) => state.regie?.arreteAnnuelCas);
  const arreteConfig = useRegieStore((state) => state.regie?.arreteConfig);

  const { setRecusLoadings } = React.useContext(LoadingContext);
  const { regies, selectedRegie } = React.useContext(RegieContext);
  const { getFactures } = React.useContext(FactureContext);

  // Liste des régisseurs de la régie sélectionnée
  const regisseurs = React.useMemo(() => {
    if (selectedRegie) {
      return _.find(regies, { identifiant: selectedRegie })?.regisseurs;
    }

    return [];
  }, [regies, selectedRegie]);

  const regieRegisseurConnecte = React.useMemo(() => {
    if (connectedRegisseur) {
      return _.find(regisseurs, { identifiant: connectedRegisseur.identifiantRegisseur });
    }

    return null;
  }, [connectedRegisseur, regisseurs]);

  const {
    items,
    setItems,
    setPage,
    loading,
    sorts,
    setSorts,
    filters,
    setFilters,
    total,
    setTotal,
    search,
    setSearch,
    reset,
  } = useListeSearchSort({
    active: !_.isNil(selectedRegie) && !standAlone && defaultFiltersApplied,
    getItems: getRecus,
    searchItems: searchRecus,
    ...(depuisDernierArrete && { fixedParams: { depuisDernierArrete: true } }),
  });

  /** Date d'annulation à transmettre, commune à l'annulation et au rejet d'un reçu. */
  const dateAnnulationCourante = React.useCallback(() => {
    const isProcedureAnnuelle = Boolean(arreteAnnuelRequis) && arreteAnnuelCas === 1;
    // Antidate à la fin d'exercice réelle (configurable : 31/12 en prod, date de test en QA),
    // pour rester aligné sur la validation backend (ArreteAnnuelCalendar::exerciceEnd).
    return isProcedureAnnuelle && arreteConfig?.exerciceEndDate
      ? `${arreteConfig.exerciceEndDate} 23:59:59`
      : dayjs().format('YYYY-MM-DD HH:mm:ss');
  }, [arreteAnnuelRequis, arreteAnnuelCas, arreteConfig]);

  /** Remplace le reçu dans la liste et rafraîchit les factures, après annulation ou rejet. */
  const appliquerRecuModifie = React.useCallback(
    (data: RecuEncaissementType) => {
      if (!standAlone) {
        setItems(
          _.map(items, (r) => {
            if (r.identifiant === data.identifiant) {
              return data;
            }

            return r;
          }),
        );
        getFactures?.();
      }

      return data;
    },
    [getFactures, items, setItems, standAlone],
  );

  const handleRecuCancel = React.useCallback(
    (recu: RecuEncaissementType) =>
      request
        .post(postAnnulationRecu(recu.identifiant), {
          dateAnnulation: dateAnnulationCourante(),
          motifAnnulation: recu?.motifAnnulation,
        })
        .then(appliquerRecuModifie),
    [appliquerRecuModifie, dateAnnulationCourante],
  );

  const handleRecuReject = React.useCallback(
    (
      recu: RecuEncaissementType,
      { motifRejet, dateLimitePaiement }: { motifRejet: string; dateLimitePaiement: string | null },
    ) =>
      request
        .post(postRejetPrelevementRecu(recu.identifiant), {
          dateAnnulation: dateAnnulationCourante(),
          motifRejet,
          ...(dateLimitePaiement && { dateLimitePaiement }),
        })
        .then(appliquerRecuModifie),
    [appliquerRecuModifie, dateAnnulationCourante],
  );

  /** Applique les filtres par défaut */
  const handleDefaultFiltersApply = React.useCallback(() => {
    if (defaultFilteredOnRegisseur) {
      if (!_.isNil(regieRegisseurConnecte)) {
        setFilters(getRecusListeDefaultFilters(regieRegisseurConnecte));
        setDefaultFiltersApplied(true);
      } else {
        // Ce cas est un cas où la liste ne charge jamais si on a pas de régisseur connecté
        // mais ça ne devrait pas arriver, sauf dans les TU
        if (process.env.NODE_ENV === 'test') {
          setDefaultFiltersApplied(true);
        }
      }
    } else {
      setFilters([]);
      setDefaultFiltersApplied(true);
    }
  }, [defaultFilteredOnRegisseur, regieRegisseurConnecte, setFilters]);

  React.useEffect(() => {
    if (!standAlone) {
      setRecusLoadings((prev) => ({ ...prev, recus: loading }));
    }
  }, [standAlone, loading, setRecusLoadings]);

  React.useEffect(() => {
    handleDefaultFiltersApply();
  }, [handleDefaultFiltersApply]);

  const context = React.useMemo<RecuContextType>(
    () => ({
      total,
      recus: items as RecuEncaissementType[],
      filters,
      search,
      sorts,
      setRecus: setItems,
      setFilters,
      setSearch,
      setSorts,
      setPage,
      setTotal,
      applyDefaultFilters: handleDefaultFiltersApply,
      getRecus: reset,
      onRecuCancel: handleRecuCancel,
      onRecuReject: handleRecuReject,
    }),
    [
      total,
      items,
      filters,
      search,
      sorts,
      setItems,
      setFilters,
      setSearch,
      setSorts,
      setPage,
      setTotal,
      reset,
      handleDefaultFiltersApply,
      handleRecuCancel,
      handleRecuReject,
    ],
  );

  return <RecuContext.Provider value={context}>{children}</RecuContext.Provider>;
};

export default RecuContextProvider;
